"use client"

import { useMemo, useState, type ReactNode } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readQuantity } from '@/lib/tools/numeric-input'

// Sources:
//   • DOE federal refrigerator energy-conservation standards (10 CFR 430):
//     successive standard revisions in 1990, 2001, and 2014 each cut typical
//     consumption — modern (post-2014) units are roughly 4× more efficient
//     than 1980s units.
//   • ENERGY STAR refrigerator key product criteria: certified models must
//     be at least 10% more efficient than the federal standard.
//   • EIA average US residential electricity rate (most recent published): ~16.5¢/kWh.

const FALLBACK_RELATED = [
  "energy-cost-calculator",
  "refrigerator-rebate-finder",
  "refrigerator-lifespan-calculator",
  "refrigerator-upgrade-savings-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  const candidates = [...slugs, ...FALLBACK_RELATED]
  for (const s of candidates) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

export function EnergyEfficiencyCalculatorPageClient({ children }: { children?: ReactNode }) {
  const meta = TOOL_METADATA["refrigerator-energy-efficiency-calculator"]

  const [currentKwh, setCurrentKwh] = useState("800")
  const [newKwh, setNewKwh] = useState("500")
  const [rate, setRate] = useState("16.5")

  const result = useMemo(() => {
    const current = readQuantity(currentKwh, { min: 100, max: 5000 })
    const newer = readQuantity(newKwh, { min: 50, max: 3000 })
    const r = readQuantity(rate, { min: 1, max: 100 })
    if (current === null || newer === null || r === null) return null

    const currentCost = (current * r) / 100
    const newCost = (newer * r) / 100
    const savings = currentCost - newCost
    const percentSavings = currentCost > 0 ? (savings / currentCost) * 100 : 0
    const tenYearSavings = savings * 10

    return {
      currentCost,
      newCost,
      savings,
      percentSavings,
      tenYearSavings,
    }
  }, [currentKwh, newKwh, rate])

  return (
    <ToolPageShell
      toolName={meta.name}
      heroEyebrow={meta.category}
      heroTitlePre="How much would a more efficient fridge"
      heroTitleItalic="actually"
      heroTitlePost="save you?"
      heroSubtitle="Compare your current fridge's annual kWh against an ENERGY STAR or newer-model number to see the dollar gap, year over year — using your own electricity rate."
      heroMeta={["⏱ ~30 sec", "DOE + ENERGY STAR + EIA data"]}
      heroShare={<ToolShareBar title={meta.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "10%+", label: "ENERGY STAR vs federal min", note: "ENERGY STAR criteria" },
            { value: "~4×", label: "1980s vs post-2014 efficiency", note: "DOE standard rounds" },
            { value: "16.5¢", label: "US avg electricity rate", note: "EIA" },
            { value: "10 yr", label: "Typical service life baseline", note: "Industry typical" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={[
        {
          question: "Where do I find my fridge's annual kWh?",
          answer:
            "On the yellow EnergyGuide label that shipped with the unit, or in the spec sheet on the manufacturer's product page. The number you want is labelled 'Estimated Yearly Electricity Use' or 'kWh/year'.",
        },
        {
          question: "What kWh range should an ENERGY STAR model fall in?",
          answer:
            "It depends on type and capacity. Modern ENERGY STAR full-size refrigerators typically draw between 400 and 700 kWh/year; compacts run lower, French-door units with through-the-door ice run higher. Check the ENERGY STAR certified product database for your size class.",
        },
        {
          question: "Is the savings number guaranteed?",
          answer:
            "No. The EnergyGuide kWh is measured under DOE test conditions. Real-world use varies with door openings, ambient temperature, ice-maker activity, and how full the unit is. Treat the result as an honest planning estimate, not a contract.",
        },
        {
          question: "Why does ENERGY STAR matter if all new fridges meet the federal standard?",
          answer:
            "Federal standards (most recently updated in 2014) set a floor. ENERGY STAR certifies models that exceed that floor by at least 10%. Over a 10-year service life, that 10% compounds into meaningful dollars at typical US rates.",
        },
        {
          question: "Should I use my off-peak rate or my average rate?",
          answer:
            "Use the rate from your most recent bill: total cost ÷ total kWh. That captures fixed charges, tiered pricing, and seasonal variation more honestly than the on-peak rate alone.",
        },
      ]}
      relatedTools={buildRelated(meta.relatedTools, "refrigerator-energy-efficiency-calculator")}
      cta={{
        title: "Ready to shop the fridges that pay back fastest?",
        body: "Browse ENERGY STAR–certified refrigerators sortable by capacity, type, and live retailer pricing.",
        buttonLabel: "Browse ENERGY STAR refrigerators",
        buttonHref: "/collections/energy-star-appliances",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            {/* Input card */}
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                  STEP 01
                </span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Old kWh.
                <br />
                New kWh.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Both numbers come from the EnergyGuide label or the manufacturer&apos;s spec sheet.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label htmlFor="refrigerator-efficiency-current-kwh" className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Current fridge — annual kWh
                  </label>
                  <div className="relative">
                    <input
                      id="refrigerator-efficiency-current-kwh"
                      type="number"
                      min={100}
                      max={5000}
                      step="1"
                      value={currentKwh}
                      onChange={(e) => setCurrentKwh(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">
                      kWh/yr
                    </span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Older units (pre-2001) often above 1,000 kWh/yr
                  </div>
                </div>

                <div>
                  <label htmlFor="refrigerator-efficiency-new-kwh" className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Replacement model — annual kWh
                  </label>
                  <div className="relative">
                    <input
                      id="refrigerator-efficiency-new-kwh"
                      type="number"
                      min={50}
                      max={3000}
                      step="1"
                      value={newKwh}
                      onChange={(e) => setNewKwh(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">
                      kWh/yr
                    </span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Modern ENERGY STAR full-size: ~400–700 kWh/yr
                  </div>
                </div>

                <div>
                  <label htmlFor="refrigerator-efficiency-rate" className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Electricity rate
                  </label>
                  <div className="relative">
                    <input
                      id="refrigerator-efficiency-rate"
                      type="number"
                      min={1}
                      max={100}
                      step="0.01"
                      value={rate}
                      onChange={(e) => setRate(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">
                      ¢/kWh
                    </span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> US avg per EIA: ~16.5¢
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            {/* Result panel */}
            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{
                    background: "rgba(255,153,81,0.35)",
                    filter: "blur(60px)",
                    transform: "translate(25%, -35%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                      Annual savings
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-white/60">
                      <RefreshCw className="w-3 h-3" /> Updated live
                    </div>
                  </div>
                  <div
                    className="font-black"
                    style={{
                      fontSize: "clamp(36px, 8vw, 88px)",
                      letterSpacing: "-0.045em",
                      lineHeight: 0.9,
                    }}
                  >
                    {`$${result?.savings.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "0"}`}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">
                      /yr
                    </span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result && result.percentSavings > 0
                      ? `That's a ${result.percentSavings.toFixed(1)}% efficiency gain — roughly $${result.tenYearSavings.toLocaleString("en-US", { maximumFractionDigits: 0 })} over a 10-year service life.`
                      : "Enter your current and replacement kWh to see savings."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Current cost
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {`$${result?.currentCost.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "—"}`}/yr
                      </div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        New cost
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {`$${result?.newCost.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "—"}`}/yr
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Compare efficiency"
          title="Counter-depth refrigerators to compare"
          description="The ENERGY STAR badge marks certified models."
          collectionType="counter-depth"
          fallbackCollectionType="french-door"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Two kWh values, <em className="italic text-[#ff9951]">one</em> rate.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Reference numbers come from DOE federal energy-conservation standards, the
                ENERGY STAR certified product database, and EIA average residential rates.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "We multiply each kWh by your rate",
                  d: "(kWh × ¢/kWh ÷ 100) gives the honest annual dollar cost — once for your current fridge, once for the replacement.",
                },
                {
                  n: "02",
                  t: "We subtract to get savings",
                  d: "Current cost − new cost = annual savings. The percent gain is savings ÷ current cost.",
                },
                {
                  n: "03",
                  t: "We project across the service life",
                  d: "Industry-typical refrigerator service life is around 10 years; the 10-year savings figure scales annual savings linearly across that span.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="French door refrigerators worth the kWh"
          description="Premium designs with the strongest efficiency gains over older units."
          collectionType="french-door"
          fallbackCollectionType="counter-depth"
        />
      </section>
      {/* Server-rendered supporting data belongs inside the shell, before its footer. */}
      {children}
    </ToolPageShell>
  )
}
